import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ReactElement } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Star, Users, Network, UserPlus, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { roleLabel, type Role } from "@/lib/admin-labels";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/uzivatele")({ component: UsersPage });

function StructureTree({
  rootId,
  rootLabel,
  referralsByReferrer,
}: {
  rootId: string;
  rootLabel: string;
  referralsByReferrer: Map<string, ProfileRow[]>;
}) {
  const renderNode = (id: string, label: string, depth: number, seen: Set<string>): ReactElement => {
    const children = seen.has(id) ? [] : referralsByReferrer.get(id) ?? [];
    const nextSeen = new Set(seen).add(id);
    return (
      <li key={id} className="py-1">
        <div className="flex items-center gap-1.5 text-sm">
          {depth > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          <span className={depth === 0 ? "font-semibold" : ""}>{label}</span>
          {children.length > 0 && (
            <span className="text-[10px] text-muted-foreground">({children.length})</span>
          )}
        </div>
        {children.length > 0 && (
          <ul className="ml-4 border-l pl-3">
            {children.map((c) => renderNode(c.id, c.full_name ?? c.email ?? "—", depth + 1, nextSeen))}
          </ul>
        )}
      </li>
    );
  };

  const total = (() => {
    let count = 0;
    const walk = (id: string, seen: Set<string>) => {
      if (seen.has(id)) return;
      seen.add(id);
      for (const c of referralsByReferrer.get(id) ?? []) {
        count++;
        walk(c.id, seen);
      }
    };
    walk(rootId, new Set());
    return count;
  })();

  if (total === 0) {
    return <p className="text-sm text-muted-foreground">Uživatel zatím nemá nikoho ve struktuře.</p>;
  }

  return (
    <div className="max-h-[50vh] overflow-auto">
      <ul>{renderNode(rootId, rootLabel, 0, new Set())}</ul>
      <p className="mt-2 text-xs text-muted-foreground">Celkem ve struktuře: {total}</p>
    </div>
  );
}

const roleVariant: Record<Role, "default" | "secondary" | "outline"> = {
  admin: "default", moderator: "default", owner: "secondary", user: "outline",
  vacanty_admin: "default", vakant_owner: "secondary",
};

type ProfileRow = {
  id: string;
  full_name: string | null;
  email: string | null;
  direct_club_member: boolean;
  owner_verified: boolean;
  created_at: string;
  referred_by: string | null;
  user_roles: { role: Role }[] | null;
  businesses: { id: string }[] | null;
};

function UsersPage() {
  const qc = useQueryClient();
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [q, setQ] = useState("");
  const [referralsFor, setReferralsFor] = useState<ProfileRow | null>(null);
  const [structureFor, setStructureFor] = useState<ProfileRow | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,full_name,email,direct_club_member,owner_verified,created_at,referred_by,user_roles(role),businesses:businesses!businesses_owner_id_fkey(id)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProfileRow[];
    },
  });

  const referralsByReferrer = useMemo(() => {
    const map = new Map<string, ProfileRow[]>();
    for (const u of users) {
      if (!u.referred_by) continue;
      const list = map.get(u.referred_by) ?? [];
      list.push(u);
      map.set(u.referred_by, list);
    }
    return map;
  }, [users]);

  const roleMut = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: Role }) => {
      const del = await supabase.from("user_roles").delete().eq("user_id", userId);
      if (del.error) throw del.error;
      const ins = await supabase.from("user_roles").insert({ user_id: userId, role });
      if (ins.error) throw ins.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const verifyMut = useMutation({
    mutationFn: async ({ userId, owner_verified }: { userId: string; owner_verified: boolean }) => {
      const { error } = await supabase.from("profiles").update({ owner_verified }).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const assignReferrerMut = useMutation({
    mutationFn: async ({ userId, referrerId }: { userId: string; referrerId: string }) => {
      const { error } = await supabase.from("profiles").update({ referred_by: referrerId }).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const unassignedUsers = useMemo(
    () => users.filter((u) => !u.referred_by),
    [users],
  );

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) =>
      (u.full_name ?? "").toLowerCase().includes(t) || (u.email ?? "").toLowerCase().includes(t),
    );
  }, [users, q]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">Uživatelé a role</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Spravujte role a ověření majitelé." : "Přehled uživatelů. Změna rolí je dostupná pouze adminovi."}
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno nebo email…" className="pl-8" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Uživatel</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Direct Club</TableHead>
                <TableHead>Ověřený majitel</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Registrace</TableHead>
                <TableHead>Pozvaní</TableHead>
                <TableHead className="text-right">Změnit roli</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">Načítám…</TableCell></TableRow>}
              {filtered.map((u) => {
                const currentRole: Role = (u.user_roles?.[0]?.role ?? "user") as Role;
                const isSelf = user?.id === u.id;
                const name = u.full_name ?? u.email ?? "—";
                const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="grid h-8 w-8 place-items-center rounded-full bg-muted text-xs font-semibold">{initials}</div>
                        <span className="font-medium">{name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{u.email ?? "—"}</span>
                        {isAdmin && (
                          <button
                            type="button"
                            title="Zobrazit strukturu"
                            onClick={() => setStructureFor(u)}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Network className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(u.businesses?.length ?? 0) > 0 ? (
                        <Badge variant="default">Firma · {u.businesses!.length} podnik{u.businesses!.length === 1 ? "" : u.businesses!.length < 5 ? "y" : "ů"}</Badge>
                      ) : (
                        <Badge variant="outline">Soukromá osoba</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.direct_club_member ? (
                        <Badge variant="default" className="gap-1"><Star className="h-3 w-3" /> Člen</Badge>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={u.owner_verified}
                        disabled={!isAdmin || verifyMut.isPending}
                        onCheckedChange={(v) => verifyMut.mutate({ userId: u.id, owner_verified: v },
                          { onSuccess: () => toast.success(v ? "Označen jako ověřený majitel" : "Ověření zrušeno") })}
                      />
                    </TableCell>
                    <TableCell><Badge variant={roleVariant[currentRole]}>{roleLabel[currentRole]}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString("cs-CZ")}</TableCell>
                    <TableCell className="min-w-[180px] max-w-[260px]">
                      {(() => {
                        const refs = referralsByReferrer.get(u.id) ?? [];
                        if (refs.length === 0) return <span className="text-muted-foreground">—</span>;
                        return (
                          <div className="space-y-0.5">
                            <button
                              type="button"
                              onClick={() => setReferralsFor(u)}
                              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            >
                              <Users className="h-3 w-3" /> {refs.length} {refs.length === 1 ? "uživatel" : refs.length < 5 ? "uživatelé" : "uživatelů"}
                            </button>
                            <ul className="text-xs text-muted-foreground">
                              {refs.slice(0, 3).map((r) => (
                                <li key={r.id} className="truncate">• {r.full_name ?? "—"}</li>
                              ))}
                              {refs.length > 3 && <li className="italic">+ {refs.length - 3} dalších…</li>}
                            </ul>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={currentRole}
                        disabled={!isAdmin || isSelf || roleMut.isPending}
                        onValueChange={(v) => roleMut.mutate({ userId: u.id, role: v as Role },
                          { onSuccess: () => toast.success(`${name}: ${roleLabel[v as Role]}`) })}
                      >
                        <SelectTrigger className="ml-auto w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="vacanty_admin">Admin Vacantů</SelectItem>
                          <SelectItem value="vakant_owner">Majitel Vacantů</SelectItem>
                          <SelectItem value="moderator">Moderátor</SelectItem>
                          <SelectItem value="owner">Majitel</SelectItem>
                          <SelectItem value="user">Uživatel</SelectItem>
                        </SelectContent>
                      </Select>
                      {isSelf && <div className="mt-1 text-[10px] text-muted-foreground">Sobě roli neměníte</div>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!referralsFor} onOpenChange={(o) => !o && setReferralsFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Pozvaní uživatelé · {referralsFor?.full_name ?? referralsFor?.email ?? "—"}
            </DialogTitle>
          </DialogHeader>
          {referralsFor && (() => {
            const refs = referralsByReferrer.get(referralsFor.id) ?? [];
            if (refs.length === 0)
              return <p className="text-sm text-muted-foreground">Žádní pozvaní uživatelé.</p>;
            return (
              <ul className="divide-y">
                {refs.map((r) => (
                  <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium">{r.full_name ?? "—"}</span>
                    {isAdmin && r.email && (
                      <span className="font-mono text-xs text-muted-foreground">{r.email}</span>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!structureFor}
        onOpenChange={(o) => {
          if (!o) {
            setStructureFor(null);
            setPickerOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="h-4 w-4" />
              Struktura · {structureFor?.full_name ?? structureFor?.email ?? "—"}
            </DialogTitle>
          </DialogHeader>

          {structureFor && (
            <StructureTree
              rootId={structureFor.id}
              rootLabel={structureFor.full_name ?? structureFor.email ?? "—"}
              referralsByReferrer={referralsByReferrer}
            />
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs text-muted-foreground">
              Dostupných nepřiřazených: {structureFor ? unassignedUsers.filter((u) => u.id !== structureFor.id).length : 0}
            </span>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" disabled={!structureFor || assignReferrerMut.isPending}>
                  <UserPlus className="mr-1 h-4 w-4" /> Přidat uživatele do struktury
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <Command>
                  <CommandInput placeholder="Hledat nepřiřazeného uživatele…" />
                  <CommandList>
                    <CommandEmpty>Žádní nepřiřazení uživatelé.</CommandEmpty>
                    <CommandGroup>
                      {structureFor &&
                        unassignedUsers
                          .filter((u) => u.id !== structureFor.id)
                          .map((u) => {
                            const label = u.full_name ?? u.email ?? "—";
                            return (
                              <CommandItem
                                key={u.id}
                                value={`${label} ${u.email ?? ""}`}
                                onSelect={() => {
                                  assignReferrerMut.mutate(
                                    { userId: u.id, referrerId: structureFor.id },
                                    {
                                      onSuccess: () => {
                                        toast.success(`${label} přidán do struktury`);
                                        setPickerOpen(false);
                                      },
                                    },
                                  );
                                }}
                              >
                                <Check className="mr-2 h-3.5 w-3.5 opacity-0" />
                                <div className="flex flex-col">
                                  <span className="text-sm">{label}</span>
                                  {u.email && (
                                    <span className="font-mono text-[10px] text-muted-foreground">{u.email}</span>
                                  )}
                                </div>
                              </CommandItem>
                            );
                          })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
